import { cx } from "@jugl-web/utils";
import { useEffect } from "react";
import { useInView } from "react-intersection-observer";

export type TableGridProps<TData> = {
  columns: {
    title: string | JSX.Element;
    content: string | JSX.Element | ((data: TData) => JSX.Element);
    width?: number;
    className?: string;
    grow?: boolean;
  }[];
  data: TData[];

  unstyled?: boolean;
  inline?: boolean;

  className?: string;
  headerCellClassName?: string;
  cellClassName?: string;

  emptyContent?: JSX.Element;
  bottomContent?: JSX.Element;
  onReachEnd?: () => void;
};

export const TableGrid = <TData,>({
  columns,
  emptyContent,
  data,
  unstyled,
  className,
  headerCellClassName,
  cellClassName,
  onReachEnd,
  bottomContent,
  inline,
}: TableGridProps<TData>) => {
  const { ref: bottomAnchorRef, inView: bottomAnchorInView } = useInView();

  useEffect(() => {
    if (bottomAnchorInView) {
      onReachEnd?.();
    }
  }, [bottomAnchorInView, onReachEnd]);

  const reachEndAnchorVisible = data.length > 0 && !!onReachEnd;

  return (
    <div
      className={cx(
        "grid",
        {
          "h-full overflow-auto": !inline && !unstyled,
          "bg-white": !unstyled,
        },
        className
      )}
      style={{
        gridTemplateRows: `min-content ${
          data.length ? data.map(() => `min-content`).join(" ") : "1fr"
        } ${bottomContent ? "auto" : ""} ${
          reachEndAnchorVisible ? "auto" : ""
        }`,
        gridTemplateColumns: columns
          .map((item, idx) =>
            item.width
              ? `${item.width}px`
              : item.grow ||
                (!columns.find((column) => column.grow) &&
                  idx === columns.length - 1)
              ? "auto"
              : `min-content`
          )
          .join(" "),
      }}
    >
      {columns.map((col, idx) => (
        <div
          key={+idx}
          className={cx(
            !unstyled &&
              "z-10 flex h-20 items-center self-start whitespace-nowrap border-x-0 border-b-[16px] border-t-[16px] border-solid border-gray-50 bg-white px-2 text-xs font-[500] text-[#93949A]",
            !unstyled && !inline && "grid-col sticky  top-0",
            !unstyled && idx === 0 && "pl-8",
            !unstyled && idx === columns.length - 1 && "pr-8",
            headerCellClassName
          )}
        >
          {col.title}
        </div>
      ))}
      {data.map((item, rowIdx) =>
        columns.map((col, idx) => (
          <div
            key={`${+rowIdx}-${+idx}`}
            className={cx(
              cellClassName,
              !unstyled && "bg-white px-2 py-4",
              !unstyled && idx === 0 && "pl-8",
              !unstyled && idx === columns.length - 1 && "pr-8",
              !unstyled &&
                rowIdx !== data.length - 1 &&
                "grid-col border-x-0 border-b-2 border-t-0 border-solid border-gray-50",
              col.className
            )}
          >
            {typeof col.content === "function"
              ? col.content(item)
              : col.content}
          </div>
        ))
      )}
      {data.length === 0 && emptyContent ? (
        <div
          style={{
            gridColumn: `span ${columns.length} / span ${columns.length}`,
          }}
        >
          {emptyContent}
        </div>
      ) : null}
      {bottomContent ? (
        <div
          style={{
            gridColumn: `span ${columns.length} / span ${columns.length}`,
          }}
        >
          {bottomContent}
        </div>
      ) : null}
      {reachEndAnchorVisible && (
        <div
          ref={bottomAnchorRef}
          style={{
            gridColumn: `span ${columns.length} / span ${columns.length}`,
          }}
          className="h-[1px]"
        />
      )}
    </div>
  );
};
